import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Camera, X, CheckCircle2 } from 'lucide-react';
import api from '../services/api';

const frecuenciaLabel = {
  DIARIO: 'Diario', SEMANAL: 'Semanal', QUINCENAL: 'Quincenal', MENSUAL: 'Mensual'
};

const estadoInfo = {
  EN_TIEMPO: { label: 'En tiempo', bg: '#f0fdf4', text: '#16a34a' },
  VENCIDO:   { label: 'Vencido', bg: '#fef2f2', text: '#dc2626' },
  FINALIZADO:{ label: 'Finalizado', bg: '#f1f5f9', text: '#475569' },
};

export default function ProyectoDetallePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [proyecto, setProyecto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAvanceForm, setShowAvanceForm] = useState(false);
  const [fotos, setFotos] = useState([]);
  const [comentario, setComentario] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchProyecto = async () => {
    const res = await api.get(`/proyectos/${id}`);
    setProyecto(res.data);
  };

  useEffect(() => {
    fetchProyecto().catch(() => navigate('/contratistas')).finally(() => setLoading(false));
  }, [id]);

  const handleAvanceSubmit = async (e) => {
    e.preventDefault();
    if (fotos.length === 0) {
      alert('Selecciona al menos una foto');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('comentario', comentario);
      fotos.forEach(f => formData.append('fotos', f));
      await api.post(`/proyectos/${id}/avances`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setShowAvanceForm(false);
      setFotos([]);
      setComentario('');
      fetchProyecto();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al subir avance');
    } finally { setSubmitting(false); }
  };

  const handleFinalizar = async () => {
    if (!window.confirm('¿Marcar este proyecto como finalizado?')) return;
    try {
      await api.patch(`/proyectos/${id}/finalizar`);
      fetchProyecto();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al finalizar proyecto');
    }
  };

  if (loading) return <p style={{ color: '#64748b' }}>Cargando...</p>;
  if (!proyecto) return null;

  const imgBase = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:4000';
  const info = estadoInfo[proyecto.estado] || estadoInfo.EN_TIEMPO;
  const esContratistaAsignado = user?.role === 'CONTRATISTA' && proyecto.contratistas.some(c => c.userId === user.id);
  const puedeFinalizar = ['ADMIN', 'DIRECTOR'].includes(user?.role) && proyecto.activo;

  return (
    <div style={{ maxWidth: 860, fontFamily: "'Segoe UI', sans-serif" }}>
      <button onClick={() => navigate('/contratistas')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20, padding: 0 }}>
        <ArrowLeft size={16} /> Volver a proyectos
      </button>

      {/* Modal subir avance */}
      {showAvanceForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 20, padding: 24, width: '100%', maxWidth: 440, boxShadow: '0 8px 40px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a2f6b' }}>Subir avance</h2>
              <button onClick={() => setShowAvanceForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={20} /></button>
            </div>
            <form onSubmit={handleAvanceSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Fotos (máximo 10)</label>
                <input type="file" accept="image/*" multiple
                  onChange={e => setFotos(Array.from(e.target.files).slice(0, 10))}
                  style={{ width: '100%', fontSize: 13, fontFamily: 'inherit' }} />
                {fotos.length > 0 && <p style={{ margin: '6px 0 0', fontSize: 12, color: '#64748b' }}>{fotos.length} foto(s) seleccionada(s)</p>}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Comentario (opcional)</label>
                <textarea value={comentario} onChange={e => setComentario(e.target.value)} rows={2}
                  style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none', resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button type="button" onClick={() => setShowAvanceForm(false)}
                  style={{ flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: 'white', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', color: '#64748b' }}>
                  Cancelar
                </button>
                <button type="submit" disabled={submitting}
                  style={{ flex: 1, padding: '11px', border: 'none', borderRadius: 10, background: '#2563eb', color: 'white', fontSize: 14, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                  {submitting ? 'Subiendo...' : 'Subir avance'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Encabezado */}
      <div style={{ background: 'white', borderRadius: 20, boxShadow: '0 2px 16px rgba(26,47,107,0.07)', border: '1px solid #e2e8f0', padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: '#1a2f6b', margin: 0 }}>{proyecto.nombre}</h1>
          <span style={{ background: info.bg, color: info.text, padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>{info.label}</span>
        </div>
        {proyecto.descripcion && <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 12px' }}>{proyecto.descripcion}</p>}

        <div style={{ background: '#f1f5f9', borderRadius: 20, height: 10, overflow: 'hidden', marginBottom: 8 }}>
          <div style={{
            width: `${proyecto.porcentaje}%`, height: '100%',
            background: proyecto.estado === 'VENCIDO' ? '#dc2626' : proyecto.porcentaje > 80 ? '#ea580c' : '#2563eb'
          }} />
        </div>
        <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}>{proyecto.porcentaje}% de avance por tiempo transcurrido</p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, fontSize: 13, color: '#64748b' }}>
          <div><span style={{ fontWeight: 600, color: '#374151' }}>Inicio: </span>{new Date(proyecto.fechaInicio).toLocaleDateString('es-MX')}</div>
          <div><span style={{ fontWeight: 600, color: '#374151' }}>Fin estimado: </span>{new Date(proyecto.fechaFin).toLocaleDateString('es-MX')}</div>
          <div><span style={{ fontWeight: 600, color: '#374151' }}>Frecuencia: </span>{frecuenciaLabel[proyecto.frecuencia]}</div>
          <div><span style={{ fontWeight: 600, color: '#374151' }}>Creado por: </span>{proyecto.createdBy?.name}</div>
        </div>

        <div style={{ marginTop: 16 }}>
          <p style={{ fontWeight: 600, color: '#374151', fontSize: 13, margin: '0 0 6px' }}>Contratistas asignados:</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {proyecto.contratistas.map(c => (
              <span key={c.id} style={{ background: '#fef9c3', color: '#854d0e', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                {c.user.name}
              </span>
            ))}
          </div>
        </div>

        {(esContratistaAsignado || puedeFinalizar) && (
          <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
            {esContratistaAsignado && proyecto.activo && (
              <button onClick={() => setShowAvanceForm(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#2563eb', color: 'white', border: 'none', borderRadius: 10, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                <Camera size={14} /> Subir avance
              </button>
            )}
            {puedeFinalizar && (
              <button onClick={handleFinalizar}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#16a34a', color: 'white', border: 'none', borderRadius: 10, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                <CheckCircle2 size={14} /> Finalizar proyecto
              </button>
            )}
          </div>
        )}
      </div>

      {/* Avances */}
      <div style={{ background: 'white', borderRadius: 20, boxShadow: '0 2px 16px rgba(26,47,107,0.07)', border: '1px solid #e2e8f0', padding: 24 }}>
        <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1a2f6b' }}>Avances reportados</h2>
        {(!proyecto.avances || proyecto.avances.length === 0) && <p style={{ color: '#94a3b8', fontSize: 13 }}>Sin avances reportados aún.</p>}
        {proyecto.avances?.map(av => (
          <div key={av.id} style={{ marginBottom: 16, padding: 14, background: '#f8fafc', borderRadius: 12 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: av.comentario ? 8 : 4 }}>
              {av.fotos.map(f => (
                <img key={f.id} src={`${imgBase}${f.url}`} alt="avance"
                  style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8, cursor: 'pointer' }}
                  onClick={() => window.open(`${imgBase}${f.url}`, '_blank')} />
              ))}
            </div>
            {av.comentario && <p style={{ margin: '0 0 4px', fontSize: 13, color: '#374151' }}>{av.comentario}</p>}
            <p style={{ margin: 0, fontSize: 11, color: '#94a3b8' }}>{av.user?.name} · {new Date(av.createdAt).toLocaleString('es-MX')}</p>
          </div>
        ))}
      </div>
    </div>
  );
}